import { Product } from '../../types';
import { User } from '../types/auth';

// --- Types ---

export interface ProductVariation {
  id: string;
  name: string; // ex: Tamanho, Cor
  value?: string; // ex: P, M, Azul
  stock?: number;
  sku?: string;
  price: number;
  salePrice?: number;
  costPrice?: number;
}

export interface AdminProduct extends Product {
  salePrice?: number;
  costPrice?: number;
  sku?: string; // Optional if has variations
  categoryId: string;
  stock?: number; // Optional if has variations
  variations?: ProductVariation[];
  discountMode?: 'manual' | 'percentage';
  discountPercent?: number;
  images?: string[] | Array<{ url: string; alt: string; order: number }>; // Support both old and new format
  weight?: number;
  dimensions?: { height: number; width: number; depth: number };
  videoUrl?: string; // YouTube or Vimeo URL
  whatsappMessage?: string;
  clicks: number;
  isProfessionalLine: boolean;
  isKit?: boolean;
  kitItems?: { productId: string; quantity: number; variantId?: string }[];
  salesType?: 'quote' | 'direct' | 'both' | 'prescription'; // quote = apenas cotação, direct = venda direta (Mercado Pago), both = ambos, prescription = receita
  createdAt: string;
  updatedAt: string;
  status: 'active' | 'inactive';
  reviews?: {
    author: string;
    rating: number;
    text: string;
    date: string;
  }[];
  arianaRecommendation?: string;
  faqs?: {
    question: string;
    answer: string;
  }[];
  rating?: number; // Average rating (calculated)
  reviewsCount?: number; // Total approved reviews (calculated)
  isAvailable?: boolean; // Manual availability override
  seoTitle?: string; // SEO optimized title
  seoDescription?: string; // SEO meta description
  seoKeywords?: string[]; // SEO keywords
  slug?: string; // SEO-friendly URL slug
  tags?: string[]; // Product tags for categorization and search
  // Price is mandatory in Product type, but we might need to relax it or handle it carefully.
  // For now, we keep it as is in Product, but in AdminProduct logic we might ignore it.
}

export interface AdminCategory {
  id: string;
  name: string;
  description: string;
  slug: string;
  productCount: number;
  parentId?: string | null; // ID of the parent category
  children?: AdminCategory[]; // Optional helper for UI, not stored in DB
  image?: string; // URL da imagem da categoria
  order: number; // Ordem de exibição
  isActive: boolean; // Status ativo/inativo
  seo?: {
    title?: string;
    description?: string;
    keywords?: string[];
  }; // Metadados SEO
  customBanner?: boolean;
  customBannerImage?: string;
  customBannerTitle?: string;
  customBannerDescription?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Coupon {
  id: string;
  code: string;
  type: 'percent' | 'fixed';
  value: number;
  status: 'active' | 'expired' | 'inactive';
  expiresAt: string;
  maxUses: number;
  usedCount: number;
}

export interface Affiliate {
  id: string;
  name: string;
  email: string;
  code: string; // e.g. AR123
  utmSource: string; // e.g. affiliate
  utmCampaign: string; // e.g. ariana
  commissionRate: number; // Percentage
  clicks: number;
  conversions: number;
  totalSales: number;
  createdAt: string;
}

export interface Customer {
  id: string;
  uid?: string; // Firebase Auth UID
  name: string;
  whatsapp: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  cpfCnpj?: string;
  instagram?: string;
  notes?: string;
  clickedProducts: string[]; // IDs
  createdAt: string;
}

export interface Quote {
  id: string;
  customerId: string;
  customerName: string;
  customerWhatsapp: string;
  customerEmail?: string;
  customerAddress?: string;
  customerCity?: string;
  customerState?: string;
  customerCpfCnpj?: string;

  // Affiliate Tracking
  affiliateId?: string | null;
  affiliateCode?: string | null;

  // Sales Tracking
  isSold?: boolean;
  finalValue?: number;
  soldAt?: string;

  items: {
    productId: string;
    name: string;
    quantity: number;
    price: number;
    image?: string;
    selectedKitVariations?: {
      [productId: string]: { id: string; label: string; productName?: string };
    };
  }[];
  totalValue: number;
  status: 'pending' | 'responded' | 'completed' | 'cancelled'; // pending = cotação aberta, responded = respondida, completed = venda realizada
  notes?: string;
  createdAt: string;
}

export interface Sale extends Quote {
  paymentStatus: 'pending' | 'approved' | 'in_process' | 'rejected' | 'cancelled' | 'refunded';
  paymentMethod?: 'pix' | 'credit_card' | 'debit_card' | 'boleto' | 'manual' | 'whatsapp';
  paymentId?: string; // Mercado Pago payment ID
  mercadoPagoData?: {
    id: string;
    status: string;
    status_detail: string;
    payment_type_id: string;
    date_approved?: string;
    date_created: string;
    transaction_amount: number;
  };
  manualPaymentNotes?: string;
  trackingCode?: string;
  nfeKey?: string;
  shippingCost?: number;
  shippingMethod?: string;
  quoteId?: string; // Link to original quote
}

export interface QuoteWithPayment extends Quote {
  paymentStatus?: 'pending' | 'paid' | 'cancelled';
  paymentMethod?: 'pix' | 'credit_card' | 'debit_card' | 'boleto' | 'cash' | 'transfer';
  paymentDate?: string;
  paymentNotes?: string;
}

export interface CustomerDetails extends Customer {
  quotes: Quote[];
  sales: Sale[];
  totalSpent: number;
  lastPurchaseDate?: string;
  tags?: string[];
}

export interface AnalyticsData {
  date: string;
  clicks: number;
  whatsappClicks: number;
  views: number;
  quotes: number;
  sales: number;
}

export interface MenuItem {
  id: string;
  label: string;
  url: string;
  type: 'header' | 'footer' | 'both' | 'topbar';
  parentId?: string | null; // For submenus
  order: number;
}

export interface Page {
  id: string;
  title: string;
  slug: string;
  content: string; // HTML content
  location: 'submenu' | 'footer' | 'none';
  createdAt: string;
  updatedAt: string;
}

export interface HomeStoryMediaItem {
  id: string;
  title: string;
  type: 'image' | 'video';
  url: string;
  posterUrl?: string;
  order: number;
  enabled?: boolean;
  productSlug?: string;
  source?: string;
}

export interface FloatingWhatsappConfig {
  enabled: boolean;
  numberMode: 'default' | 'custom';
  customNumber?: string;
  message: string;
  productMessageEnabled: boolean;
  productMessageTemplate: string;
  showProductHelpText: boolean;
  productHelpText: string;
}

export interface Permission {
  id: string;
  name: string;
  description: string;
  code: string; // e.g. 'products.read', 'products.write'
}

export interface StoreConfig {
  name: string;
  description: string;
  quotationMode?: boolean; // Global "Quotation Only" mode (hides prices, disables direct buy)
  colors: {
    primary: string;
    secondary: string;
    accent: string;
  };
  featuredProducts?: string[]; // IDs of products to show in Home Featured section
  bestSellerProducts?: string[]; // IDs of products to show in Home Best Sellers section
  banners: {
    hero: {
      image: string;
      mobileImage?: string;
      title: string;
      subtitle: string;
      link: string;
      tag?: string;
      align?: 'left' | 'center' | 'right';
      showButton?: boolean;
      buttonText?: string;
      headerColor?: 'white' | 'dark';
      mobileBlur?: number; // 0-20 (blur-[value])
      desktopBlur?: number; // 0-20
      mobileOpacity?: number; // 0-100
      desktopOpacity?: number; // 0-100
    }[];
    heroInterval: number; // ms
    mid: { image: string; title: string; link: string }[];
    footer: { image: string; title: string; link: string }[];
  };
  catalogBanners?: {
    products: string;
    kits: string;
    equipment: string;
  };
  benefitsStrip: {
    enabled: boolean;
    items: { icon: string; title: string; subtitle: string }[];
  };
  educationBlock: {
    enabled: boolean;
    tag: string;
    title: string;
    highlightedTitle: string;
    description: string;
    backgroundImage: string;
    mobileBackgroundImage?: string;
    gradientFrom: string; // e.g., 'white'
    gradientTo: string; // e.g., 'transparent'
    priceLabel?: string;
    priceValue?: string;
    secondaryText?: string;
    cardBackgroundColor?: string;
    cardTextColor?: string;
    items: {
      icon: 'graduation' | 'star' | 'award' | 'book';
      title: string;
      description: string;
    }[];
    primaryButton: {
      text: string;
      link: string;
      backgroundColor: string;
      textColor: string;
    };
  };
  topbar: {
    enabled: boolean;
    animated: boolean;
    text: string;
    backgroundColor: string;
    textColor: string;
    links?: { label: string; url: string }[];
  };
  buyButton: {
    text: string;
    icon: string; // Lucide icon name
    showIcon: boolean;
  };
  integrations: {
    googleAnalytics: string;
    facebookPixel: string;
    cloudinary: {
      cloudName: string;
      apiKey: string;
      apiSecret: string;
    };
    mercadopago?: {
      publicKey: string;
      accessToken: string;
    };
    geminiApiKey?: string;
    melhorEnvio?: {
      clientId: string;
      clientSecret: string;
      accessToken?: string;
      refreshToken?: string;
      tokenExpiresAt?: number; // Timestamp
      sandbox?: boolean;
    };
    bling?: {
      clientId?: string;
      clientSecret?: string;
      apiKey?: string; // v2
      accessToken?: string; // v3
      refreshToken?: string; // v3
      expiresIn?: string; // v3
    };
    googleMerchant?: {
      lastSync?: string; // ISO Date
    };
  };
  shipping?: {
    enableFreeShipping: boolean;
    freeShippingThreshold: number;
    freeShippingRegions?: string[]; // e.g. ['BR'] for all, or ['SP', 'RJ']
    enableStorePickup?: boolean; // Allow customers to pick up in-store
    enableProductShippingCalculator?: boolean; // Show shipping calculator on product and kit pages
  };
  ai?: {
    geminiApiKey?: string;
    googleSearchApiKey?: string;
    googleSearchCx?: string;
    autoBlogEnabled: boolean;
    postsPerDay: number;
    model?: string;
  };
  contacts: {
    whatsapp: string;
    email: string;
    phone: string;
    address: string;
  };
  floatingWhatsapp?: FloatingWhatsappConfig;
  payments?: {
    enableGatewayPayment: boolean;
    enableWhatsappPayment: boolean;
  };
  homeSections?: {
    featured: {
      enabled: boolean;
      title: string;
      accentText: string;
      subtitle?: string;
      viewAllText: string;
    };
    bestSellers: {
      enabled: boolean;
      title: string;
      accentText: string;
      subtitle?: string;
      viewAllText: string;
      autoMode: 'manual' | 'popular';
    };
    stories: {
      enabled: boolean;
      title: string;
      subtitle?: string;
      source?: 'generated' | 'admin';
      items?: HomeStoryMediaItem[];
    };
    newsletter: {
      enabled: boolean;
      title: string;
      subtitle: string;
      buttonText: string;
      namePlaceholder: string;
      phonePlaceholder: string;
      emailPlaceholder: string;
      disclaimer?: string;
    };
    social: {
      enabled: boolean;
      title: string;
      handle: string;
      subtitle?: string;
    };
  };
}

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string; // HTML or Markdown
  featuredImage?: string;
  author: string;
  authorId: string;
  category?: string;
  tags: string[];
  status: 'draft' | 'published';
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
  views: number;
  seo?: {
    metaTitle?: string;
    metaDescription?: string;
    keywords?: string[];
  };

  // Campos para conversão e vendas
  relatedProducts?: {
    productId: string;
    featured: boolean; // Se é o produto principal do post
    position?: 'top' | 'middle' | 'bottom'; // Onde aparece no post
  }[];

  arianaRecommendation?: {
    text: string;
    productId?: string; // Produto específico que ela recomenda
    image?: string; // Foto da Ariana (opcional)
  };

  productFAQ?: {
    question: string;
    answer: string;
  }[];

  tableOfContents?: {
    title: string;
    anchor: string;
  }[];

  complementaryProducts?: string[]; // IDs de produtos complementares
  linkedCategoryId?: string; // ID da categoria vinculada para exibir produtos
}

// --- Backend Interface ---

export interface IBackend {
  // Products
  getProducts(): Promise<AdminProduct[]> | AdminProduct[];
  getProduct(id: string): Promise<AdminProduct | undefined> | AdminProduct | undefined;
  updateProduct(
    id: string,
    updates: Partial<AdminProduct>
  ): Promise<AdminProduct | null> | AdminProduct | null;
  createProduct(
    product: Omit<AdminProduct, 'id' | 'createdAt' | 'updatedAt' | 'clicks'>
  ): Promise<AdminProduct> | AdminProduct;
  deleteProduct(id: string): Promise<void> | void;
  incrementProductClicks(id: string): Promise<void> | void;

  // Categories
  getCategories(): Promise<AdminCategory[]> | AdminCategory[];
  getCategory(id: string): Promise<AdminCategory | undefined> | AdminCategory | undefined;
  createCategory(
    category: Omit<AdminCategory, 'id' | 'createdAt' | 'updatedAt' | 'productCount'>
  ): Promise<AdminCategory> | AdminCategory;
  updateCategory(
    id: string,
    updates: Partial<AdminCategory>
  ): Promise<AdminCategory | null> | AdminCategory | null;
  deleteCategory(id: string): Promise<void> | void;

  // Coupons
  getCoupons(): Promise<Coupon[]> | Coupon[];
  createCoupon(coupon: Omit<Coupon, 'id' | 'usedCount'>): Promise<Coupon> | Coupon;
  updateCoupon(id: string, updates: Partial<Coupon>): Promise<Coupon | null> | Coupon | null;
  deleteCoupon(id: string): Promise<void> | void;

  // Affiliates
  getAffiliates(): Promise<Affiliate[]> | Affiliate[];
  createAffiliate(
    affiliate: Omit<Affiliate, 'id' | 'createdAt' | 'clicks' | 'conversions' | 'totalSales'>
  ): Promise<Affiliate> | Affiliate;
  updateAffiliate(
    id: string,
    updates: Partial<Affiliate>
  ): Promise<Affiliate | null> | Affiliate | null;
  deleteAffiliate(id: string): Promise<void> | void;
  getAffiliateByCode(code: string): Promise<Affiliate | undefined> | Affiliate | undefined;
  incrementAffiliateClicks(id: string): Promise<void> | void;

  // Customers
  getCustomers(): Promise<Customer[]> | Customer[];
  getCustomerDetails(
    id: string
  ): Promise<CustomerDetails | undefined> | CustomerDetails | undefined;
  updateCustomer(
    id: string,
    updates: Partial<Customer>
  ): Promise<Customer | null> | Customer | null;
  getCustomerQuotes(customerId: string): Promise<Quote[]> | Quote[];
  getCustomerSales(customerId: string): Promise<Sale[]> | Sale[];
  deleteCustomer(id: string): Promise<void> | void;

  // Newsletter
  getNewsletterSubscribers(): Promise<NewsletterSubscriber[]> | NewsletterSubscriber[];
  upsertNewsletterSubscriber(
    subscriber: Pick<NewsletterSubscriber, 'name' | 'phone' | 'email'> &
      Partial<Pick<NewsletterSubscriber, 'source'>>
  ): Promise<NewsletterSubscriber> | NewsletterSubscriber;
  updateNewsletterSubscriber(
    id: string,
    updates: Partial<NewsletterSubscriber>
  ): Promise<NewsletterSubscriber | null> | NewsletterSubscriber | null;
  deleteNewsletterSubscriber(id: string): Promise<void> | void;

  // Quotes
  getQuotes(): Promise<Quote[]> | Quote[];
  createQuote(quote: Omit<Quote, 'id' | 'createdAt' | 'status'>): Promise<Quote> | Quote;
  updateQuote(id: string, updates: Partial<Quote>): Promise<Quote | null> | Quote | null;

  // Sales
  getSales(): Promise<Sale[]> | Sale[];
  createSale(sale: Omit<Sale, 'id' | 'createdAt'>): Promise<Sale> | Sale;
  updateSale(id: string, updates: Partial<Sale>): Promise<Sale | null> | Sale | null;
  syncMercadoPagoPayment(paymentId: string): Promise<Sale | null> | Sale | null;
  deductStock(saleId: string): Promise<void> | void;

  // Analytics
  getDashboardMetrics(startDate?: Date, endDate?: Date): Promise<any> | any;
  incrementWhatsappClicks(id: string): Promise<void> | void;
  trackVisit(): Promise<void> | void;
  getAnalytics(): Promise<AnalyticsData[]> | AnalyticsData[];
  getQuoteMetrics(startDate?: Date, endDate?: Date): Promise<any> | any;
  getSaleMetrics(startDate?: Date, endDate?: Date): Promise<any> | any;

  // Store Config
  getStoreConfig(): Promise<StoreConfig> | StoreConfig;
  updateStoreConfig(config: Partial<StoreConfig>): Promise<StoreConfig> | StoreConfig;

  // Menus
  getMenus(): Promise<MenuItem[]> | MenuItem[];
  updateMenus(menus: MenuItem[]): Promise<MenuItem[]> | MenuItem[];

  // Pages
  getPages(): Promise<Page[]> | Page[];
  getPage(id: string): Promise<Page | undefined> | Page | undefined;
  getPageBySlug(slug: string): Promise<Page | undefined> | Page | undefined;
  createPage(page: Omit<Page, 'id' | 'createdAt' | 'updatedAt'>): Promise<Page> | Page;
  updatePage(id: string, updates: Partial<Page>): Promise<Page | null> | Page | null;
  deletePage(id: string): Promise<void> | void;

  // Users
  login(
    email: string,
    password: string
  ): Promise<{ user: any; token: string } | null> | { user: any; token: string } | null;
  getUsers(): Promise<User[]> | User[];
  createUser(user: Omit<User, 'id'>, password?: string): Promise<User> | User;
  updateUser(id: string, updates: Partial<User>): Promise<User | null> | User | null;
  deleteUser(id: string): Promise<void> | void;

  // Permissions
  getPermissions(): Promise<Permission[]> | Permission[];
  createPermission(permission: Omit<Permission, 'id'>): Promise<Permission> | Permission;
  updatePermission(
    id: string,
    updates: Partial<Permission>
  ): Promise<Permission | null> | Permission | null;
  deletePermission(id: string): Promise<void> | void;

  // Blog Posts
  getBlogPosts(): Promise<BlogPost[]> | BlogPost[];
  getBlogPost(id: string): Promise<BlogPost | undefined> | BlogPost | undefined;
  getBlogPostBySlug(slug: string): Promise<BlogPost | undefined> | BlogPost | undefined;
  createBlogPost(
    post: Omit<BlogPost, 'id' | 'createdAt' | 'updatedAt' | 'views'>
  ): Promise<BlogPost> | BlogPost;
  updateBlogPost(
    id: string,
    updates: Partial<BlogPost>
  ): Promise<BlogPost | null> | BlogPost | null;
  deleteBlogPost(id: string): Promise<void> | void;
  incrementBlogPostViews(id: string): Promise<void> | void;

  // Reviews
  createReview(review: Omit<Review, 'id' | 'createdAt' | 'likes'>): Promise<Review> | Review;
  getProductReviews(productId: string): Promise<Review[]> | Review[];
  getAllReviews(): Promise<Review[]> | Review[]; // For admin
  updateReviewStatus(
    id: string,
    status: 'pending' | 'approved' | 'rejected',
    reply?: string
  ): Promise<Review | null> | Review | null;
  approveAllReviews(productId: string): Promise<void> | void;
  deleteReview(id: string): Promise<void> | void;
  likeReview(id: string): Promise<void> | void;

  // Questions
  createQuestion(
    question: Omit<Question, 'id' | 'createdAt' | 'status'>
  ): Promise<Question> | Question;
  getProductQuestions(productId: string): Promise<Question[]> | Question[];
  getAllQuestions(): Promise<Question[]> | Question[]; // For admin
  answerQuestion(id: string, answer: string): Promise<Question | null> | Question | null;
  deleteQuestion(id: string): Promise<void> | void;
  approveQuestion(id: string): Promise<Question | null> | Question | null; // Just approve without answer (if needed) or reject

  // Migration
  migrateFromLocalStorage(): Promise<string>;
  seedDatabase(): Promise<void>;
  seedProductsFromConstants?(): Promise<string>;
  clearDatabase(): Promise<void>;
}

// --- Reviews & Questions Types ---

export interface Review {
  id: string;
  productId: string;
  productName?: string; // Helper for admin
  productImage?: string; // Helper for admin
  customerId?: string;
  customerName: string;
  rating: number; // 1-5
  comment: string;
  photos?: string[];
  videoUrl?: string;
  createdAt: string;
  status: 'pending' | 'approved' | 'rejected';
  verifiedPurchase: boolean;
  likes: number;
  reply?: string;
  repliedAt?: string;
}

export interface Question {
  id: string;
  productId: string;
  productName?: string; // Helper for admin
  productImage?: string; // Helper for admin
  customerName: string;
  email: string;
  question: string;
  answer?: string;
  answeredAt?: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

export interface NewsletterSubscriber {
  id: string;
  name?: string;
  phone?: string;
  email: string;
  emailNormalized: string;
  status: 'active' | 'unsubscribed';
  source: string;
  createdAt: string;
  updatedAt: string;
}

// --- Firebase Backend Export ---

import { FirebaseBackend } from './firebaseBackend';

export const db = new FirebaseBackend();
